import React, { Fragment, useEffect, useMemo, useState, } from 'react';
import { uniq, sortBy, sumBy, orderBy, chunk, groupBy, get, pick, isEmpty, keyBy, omit, } from 'lodash';
import numeral from 'numeral';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, } from 'reactstrap';
import { toast } from 'react-toastify';
import { addDays, startOfDay, endOfDay, parse as parseDate, isValid as isValidDate, format as formatDate } from 'date-fns';
import { useToggle, useList, useCounter, } from 'react-use';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';
import dedent from 'dedent';
import classnames from 'classnames';

import firebase, { functions } from '../../firebase';
import { batch, getCollectionData, getDocumentData, } from '../../shared/firebase';
import { fields, types, statuses, boxSizes, shippingOptions, deliveryTypes, deliveryType, computeDocumentNumberForBarcode, } from '../../shared/models/shippingRequest';
import { smallImageUrl, } from '../../shared/models/amazonProduct';
import { priceTaxTypes, } from '../../shared/models/product';
import { idWithTenant, getNextShippingRequestId, getNextShippingRequestIds, serial, } from '../../shared/util';
import { deliveryTimes, prefectures, prefecturesByName, romaPrefectures, } from '../../shared/config';
import { fieldDisplayValue, } from '../../shared/util';
import { newId, fullPathWithParams, getAddressByPostalCode, } from '../../util';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ShippingRequestFormModal from '../modals/ShippingRequestFormModal';
import ShippingRequestTypeBadge from '../ShippingRequestTypeBadge';
import AddButton from '../AddButton';
import ImportButton from '../ImportButton';
import ExportButton from '../ExportButton';
import QuerySelector from '../QuerySelector';
import QueryInput from '../QueryInput';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import Pagination from '../Pagination';
import FilesUploadButton from '../FilesUploadButton';
import DateSelector from '../DateSelector';
// import QuerySortLabel from '../QuerySortLabel';
import ProductTagsDisplay from '../ProductTagsDisplay';
import ShippingRequestStatusComputationDetailButton from '../ShippingRequestStatusComputationDetailButton';

const countPerPage = 100;
const QuerySortLabel = 'span';
const db = firebase.firestore();
const createFulfillmentInboundShipmentFromPlan = functions.httpsCallable('createFulfillmentInboundShipmentFromPlan', { timeout: 550000, });
const storageRef = firebase.storage().ref();
const { values, keys, entries } = Object;
const manualShipmentDeliveryTypesByLabel = keyBy(entries(omit(deliveryTypes, ['amazon'])).map(([k, v]) => ({ key: k, ...v })), 'label');
const deliveryTimesByFormat = keyBy(entries(omit(deliveryTimes, ['sagawa1821'])).map(([k, v]) => ({ key: k, ...v })), 'format');
const shippingOptionsByLabel = keyBy(entries(shippingOptions).map(([k, v]) => ({ key: k, ...v })), 'label');
const shippingOptionOptions = entries(shippingOptions).map(([k, v]) => ({ label: v.label, value: k }));
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
  status: _ => keys(statuses).indexOf(_.status),
  completedAt: _ => _.completedAt ? _.completedAt.toDate() : new Date(),
  shippingOptionValues: _ => fieldDisplayValue(_.shippingOptionValues, fields({ type: _.type, }).shippingOptionValues) || '',
};
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));

export default Page(function ShippingRequests (props) {
  const { location, history, tenant } = props;
  const [componentVersion, { inc: updateComponentVersion }] = useCounter(0);
  const queryParams = useQueryParams();
  const {
    startOn: startOnString,
    endOn: endOnString,
    shippingOptions: shippingOptionsForFilter = [],
    newItems,
    sort = { field: 'id', direction: 'desc', },
    statuses: statusesForFilter = [],
    sku: skuForFilter,
    keyword,
  } = queryParams;
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const isSearching = !isEmpty(skuForFilter) || !isEmpty(keyword);
  const generalSetting = useDocumentSubscription(db.collection('settings').doc('general'));
  const productChunks = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id]);
  const shippingRequestChunks = useCollectionSubscription(tenant.ref.collection('shippingRequestChunks'), [tenant.id], { initialItems: null });
  const hasLoadedShippingRequestChunks = shippingRequestChunks != null;
  const chunkProducts = useMemo(_ => productChunks.flatMap(_ => entries(omit(_, ['id', 'ref'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, }))), [productChunks]);
  const chunkShippingRequests = orderBy(shippingRequestChunks?.flatMap(_ => entries(_.data || {}).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('shippingRequests').doc(k), ...v, }))), _ => numeral(_.id).value(), 'desc');
  const products = chunkProducts;
  const productsById = keyBy(chunkProducts, 'id');
  const productsGroupedBySku = useMemo(_ => groupBy(chunkProducts, 'sku'), [chunkProducts]);
  const productsByExtraSellerIdAndSku = keyBy(products, _ => [_.extraSellerId, _.sku].join('__'));
  const sendersRef = tenant.ref.collection('senders');
  const senders = useCollectionSubscription(sendersRef.orderBy('createdAt'), [tenant.id]);
  const sendersById = keyBy(senders, 'id');
  const sendersByCode = keyBy(senders, 'code');
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const extraSellers = useCollectionSubscription(tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const extraSellersByName = keyBy([{ ...tenant, name: 'メイン', id: null, }, ...extraSellers], 'name');
  const extraSellersById = keyBy(Object.values(extraSellersByName), 'id');
  const skuSearchedProducts = useMemo(_ => productsGroupedBySku[skuForFilter] || [], [productsGroupedBySku, skuForFilter]);

  const generateShippingRequestItems = (shippingRequests) => {
    return shippingRequests.map((shippingRequest) => {
      const { shippingItems = [], items = [], senderId, status, } = shippingRequest;
      const sender = sendersById[senderId];
      const itemsWithProduct = items.map((item, i) => {
        const { productId, } = item;
        const product = productsById[productId];
        return { ...item, product };
      }).filter(_ => _.product);
      const isImmutable = ['processing', 'shipped', 'discarded', 'cancelling', 'cancelled'].includes(status);
      return {
        ...shippingRequest,
        sender,
        itemsWithProduct,
        isImmutable,
        deliveryType: deliveryType(generalSetting, shippingRequest, productsById, tenant.allowsMultiNeko),
      };
    });
  };

  let filteredChunkShippingRequests = chunkShippingRequests;
  if(isSearching) {
    if(!isEmpty(skuForFilter)) {
      // filteredChunkShippingRequests = filteredChunkShippingRequests.filter(_ => (_.productIds || []).map(_ => productsById[_]?.sku || '').some(_ => _.includes(skuForFilter)));
    }
    if(!isEmpty(keyword)) {
      filteredChunkShippingRequests = filteredChunkShippingRequests.filter(_ => _.search?.includes(keyword));
    }
  } else {
    if(!isEmpty(startOnString)) {
      filteredChunkShippingRequests = filteredChunkShippingRequests.filter(_ => _.completedAt?.toDate() >= startOfDay(startOn));
    }
    if(!isEmpty(endOnString)) {
      filteredChunkShippingRequests = filteredChunkShippingRequests.filter(_ => _.completedAt?.toDate() <= endOfDay(endOn));
    }
    if(!isEmpty(shippingOptionsForFilter)) {
      filteredChunkShippingRequests = filteredChunkShippingRequests.filter((chunkShippingRequest) => {
        return chunkShippingRequest.shippingOptionValues?.some(_ => shippingOptionsForFilter.includes(_));
      });
    }
    if(!isEmpty(statusesForFilter)) {
      filteredChunkShippingRequests = filteredChunkShippingRequests.filter(_ => statusesForFilter.includes(_.status));
    }
  }
  const pagination = usePagination({ countPerPage, totalCount: filteredChunkShippingRequests.length, });
  const { pageNumber, offset, } = pagination;
  const { items: shippingRequestsByChunks, } = useDocumentsFetch(filteredChunkShippingRequests.slice(offset, offset + countPerPage).map(_ => _.ref), [tenant, shippingRequestChunks, productChunks, pageNumber, componentVersion, startOnString, endOnString, skuForFilter, keyword, shippingOptionsForFilter.join(''), statusesForFilter.join('')], { detail: true });
  const { items: shippingRequestsBySku, } = useCollectionSubscription(skuSearchedProducts?.length > 0 && tenant.ref.collection('shippingRequests').where('productIds', 'array-contains-any', skuSearchedProducts.map(_ => _.id)), [skuSearchedProducts], { detail: true });
  const shippingRequests = useMemo(_ => queryParams.sku ? shippingRequestsBySku : shippingRequestsByChunks, [queryParams.sku, shippingRequestsBySku, shippingRequestsByChunks]);
  const filteredShippingRequestItems = generateShippingRequestItems(shippingRequests);
  const sortedShippingRequestItems = orderBy(filteredShippingRequestItems, (sortIterateees[sort.field] || sort.field), sort.direction);

  const validate = async (row) => {
    const {
      ショップ識別名: extraSellerName,
      SKU: sku,
      数量: _goodQuantity,
      toB出荷: _isToBe,
      郵便番号: _postalCode,
      住所: address,
      宛名: name,
      電話番号: _phone,
      配送日: _deliveryDate,
      配送時間: _deliveryTime,
      オプション: _shippingOptionValues = '',
      注文番号: orderNumber = '',
      配送元: senderCode,
      メモ: note,
    } = row;
    if(isEmpty((sku || '').trim())) {
      return { source: row, errors: ['SKUは必須です'] };
    }
    const isToBe = _isToBe === 'はい';
    const postalCode = numeral(_postalCode.replace(/-/g, '')).format('0000000');
    const phone = _phone.replace(/-/g, '');
    const { product, extraSeller, hasMatchedMultiProducts, } = (() => {
      if(extraSellerName) {
        const extraSeller = extraSellersByName[extraSellerName];
        const product = productsByExtraSellerIdAndSku[[extraSeller?.id, sku].join('__')];
        return { extraSeller, product };
      } else {
        const mainExtraSeller = extraSellersByName['メイン'];
        const mainExtraSellerProduct = productsByExtraSellerIdAndSku[[mainExtraSeller.id, sku].join('__')];
        if(mainExtraSellerProduct != null) {
          return { extraSeller: mainExtraSellerProduct, product: mainExtraSellerProduct, };
        } else {
          const products = productsGroupedBySku[sku] ?? [];
          if(products.length > 1) {
            return { hasMatchedMultiProducts: true, };
          } else if (products.length === 1) {
            const product = products[0];
            const extraSeller = extraSellersById[product.extraSellerId];
            return { extraSeller, product };
          } else {
            return { extraSeller: mainExtraSellerProduct, product: null, };
          }
        }
      }
    })();
    const goodQuantity = parseInt(_goodQuantity, 10);
    const deliveryDate = isEmpty(_deliveryDate) ? null : parseDate(_deliveryDate, 'yyyy/MM/dd', startOfDay(new Date()));
    const deliveryTime = get(deliveryTimesByFormat, [_deliveryTime, 'key']);
    const shippingOptionValues = _shippingOptionValues.replace(/\s　/g, '').split(',').filter(_ => _).map(_ => get(shippingOptionsByLabel, [_, 'key']));
    const senderId = get(sendersByCode, [senderCode, 'id']);
    const addresses = await (async () => {
      try {
        return await getAddressByPostalCode(postalCode);
      } catch(e) {
        console.log(e);
        return [];
      }
    })();
    const addressData = addresses.find(_ => address.match(prefectures[_.prefecture]) && address.match(_.city));
    const postalCodeMatched = addressData != null;
    const errors = [
      hasMatchedMultiProducts && 'SKUが複数存在しました。ショップを識別してください',
      !extraSeller && 'ショップ識別名が正しくありません',
      !product && 'SKUが正しくありません',
      product?.isSet && 'セット品は出荷依頼できません',
      !(goodQuantity >= 0) && '数量が正しくありません',
      !(postalCode || '').match(/^[0-9]{7}$/g) && '郵便番号が正しくありません',
      isEmpty(addresses) && '郵便番号が存在しません',
      !isEmpty(addresses) && !postalCodeMatched && '郵便番号と住所が一致しません',
      !address && '住所は必須です',
      !name && '宛名は必須です',
      (phone && !phone.match(/^\d+$/)) && '電話番号が正しくありません',
      deliveryDate != null && (!isValidDate(deliveryDate) || deliveryDate < startOfDay(addDays(new Date(), 1))) && '配送日が正しくありません',
      !deliveryTime && '配送時間が正しくありません',
      shippingOptionValues.some(_ => !_) && 'オプションが正しくありません',
      !senderId && '配送元が正しくありません',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      extraSellerId: extraSeller?.id,
      productId: get(product, 'id'),
      goodQuantity,
      isToBe,
      postalCode,
      prefecture: addressData?.prefecture,
      city: addressData?.city,
      address1: address.replace(prefectures[addressData?.prefecture], '').replace(addressData?.city, ''),
      address2: '',
      name,
      phone,
      deliveryDate,
      deliveryTime,
      shippingOptionValues,
      orderNumber,
      senderId,
      note,
    }
  };
  const processRows = async (rows) => {
    const validatedRows = (await chunk(rows, 10)
      .reduce(async (x, rows) => {
        const prevs = await x;
        return [...prevs, (await Promise.all(rows.map(validate)))];
      }, Promise.resolve([])))
      .flat();
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {
      const groupedRows = groupBy(validatedRows, row => [
        'postalCode', 'prefecture', 'city', 'address1', 'address2',
        'name', 'deliveryDate', 'deliveryTime',
      ].map(_ => row[_]).join('--'));
      // 採番をまとめすぎると、書き込みに失敗した際に確保済みのIDがまとめて欠番になる。
      await serial(chunk(values(groupedRows), 500), async (rowsGroups) => {
        const ids = await getNextShippingRequestIds(tenant.ref, db, rowsGroups.length);
        await serial(chunk(rowsGroups.map((rows, i) => ({ id: ids[i], rows })), 100), async (shippingRequests) => {
          await Promise.all(shippingRequests.map(async ({ id, rows }) => {
            const [row] = rows;
            const items = entries(groupBy(rows, 'productId')).map(([productId, rows]) => ({ productId, goodQuantity: sumBy(rows, 'goodQuantity') }));
            await shippingRequestsRef.doc(id).set({
              type: 'normal',
              ...omit(row, ['source', 'errors', 'productId', 'goodQuantity']),
              orderNumber: uniq(rows.map(_ => _.orderNumber).filter(_ => _)).join(','),
              items,
              status: 'initial',
              productIds: items.map(_ => _.productId),
              createdAt: new Date(),
            });
          }));
        });
      });
    }
  };
  const onClickDownloadSampleCsv = () => {
    const data = [{
      ...(tenant.tenantOptionValues?.includes('multiSellers') && { ショップ識別名: 'メイン' }),
      SKU: 'sku_123456',
      数量: 1,
      toB出荷: 'はい',
      郵便番号: '5900001',
      住所: '大阪府堺市堺区遠里小野1-1-1 マンション101',
      宛名: 'テスト太郎',
      電話番号: '080-1111-2222',
      配送日: formatDate(addDays(new Date(), 10), 'yyyy/MM/dd'),
      配送時間: '14-16',
      オプション: 'OPP梱包,プチプチ梱包,着払いで出荷',
      注文番号: '1234,1235',
      配送元: senders.find(_ => _.isDefault)?.code || '(配送元の識別名を入力してください)',
      メモ: '',
    }];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, '出荷依頼サンプル.csv');
  };
  const rowsForExport = async () => {
    const shippingRequests = await chunk(filteredChunkShippingRequests, 100).reduce(async (x, chunkShippingRequests, i) => {
      const prevs = await x;
      const [items] = await Promise.all([
        Promise.all(chunkShippingRequests.map(_ => getDocumentData(_.ref))),
        new Promise(_ => setTimeout(_, 2000)),
      ]);
      return [...prevs, ...items];
    }, Promise.resolve([]));
    return generateShippingRequestItems(shippingRequests).map((shippingRequestItem) => {
      const { id, ref, type = 'normal', shippingItems = [], items = [], senderId, orderNumber, postalCode, prefecture, city, address1, address2, name, phone, deliveryDate, deliveryTime = 'free', shippingOptionValues = [], status, sender, itemsWithProduct, createdAt, completedAt, deliveryType, isHiddenDeliveryType = false, plan, note, } = shippingRequestItem;
      return {
        出荷依頼ID: `${tenant.id}-${id}`,
        出荷依頼日時: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        出荷日: completedAt && formatDate(completedAt.toDate(), 'yyyy/MM/dd'),
        配送方法: isHiddenDeliveryType ? null : deliveryTypes[deliveryType].label,
        配送番号: plan?.ShipmentId,
        SKU: itemsWithProduct.map(_ => _.product.sku).join(','),
        商品名: itemsWithProduct.map(_ => _.product.name).join(','),
        出荷数量: itemsWithProduct.map(_ => _[types[type].quantityName]).join(','),
        販売価格: itemsWithProduct.map(_ => _.product.price ?? '').join(','),
        税区分: itemsWithProduct.map(_ => priceTaxTypes[_.product.priceTaxType]?.label ?? '').join(','),
        郵便番号: postalCode,
        都道府県: prefectures[prefecture],
        市区郡町村: city,
        それ以降の住所: address1 + address2,
        宛名: name,
        電話番号: phone,
        配送日: fieldDisplayValue(deliveryDate, fields({ tenant, type }).deliveryDate),
        配送時間: fieldDisplayValue(deliveryTime, fields({ tenant, type }).deliveryTime),
        オプション: fieldDisplayValue(shippingOptionValues, fields({ tenant, type }).shippingOptionValues),
        注文番号: orderNumber,
        配送元: sender && sender.code,
        伝票番号: shippingItems.map(_ => type === 'amazon' ? computeDocumentNumberForBarcode(shippingRequestItem) : _.documentNumber).join(','),
        ステータス: statuses[status].label,
        メモ: note,
      };
    });
  };
  const newItemRef = async () => {
    const id = await getNextShippingRequestId(tenant.ref, db);
    return shippingRequestsRef.doc(id);
  };
  const processNewValues = async (values) => {
    const processedFiles = await Promise.all(Array.from(values.files || []).map(async (file) => {
      const path = `tenants/${tenant.id}/shippingRequests/${values.id}/files/${(new Date()).getTime()}`;
      const fileRef = storageRef.child(path);
      await fileRef.put(file, { contentType: file.type });
      const downloadUrl = await fileRef.getDownloadURL();
      return {
        uploadedAt: new Date(),
        downloadUrl,
        storagePath: path,
        ...pick(file, ['name', 'type']),
      };
    }));
    return {
      ...omit(values, 'files'),
      files: processedFiles,
      status: 'initial',
      productIds: values.items.map(_ => _.productId),
    };
  };
  useEffect(() => {
    if(isEmpty(statusesForFilter)) {
      const path = fullPathWithParams({ statuses: ['initial', 'pending', 'waitingToReceive', 'waitingToShip', 'waitingToDiscard', 'processing', 'cancelling'], }, location);
      history.replace(encodeURI(path));
    }
  }, []);

  return (
    <div className="shipping-requests container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <div className="card p-2">
            <div className="d-flex align-items-center flex-wrap gap-1">
              <DateSelector paramName="startOn" label="出荷日(開始)" disabled={isSearching} />
              <DateSelector paramName="endOn" label="出荷日(終了)" invalid={startOn > endOn} disabled={isSearching} />
            </div>
            <div className="d-flex align-items-end flex-wrap gap-1">
              <QuerySelector width={500} paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" disabled={isSearching} />
              <QuerySelector width={180} paramName="shippingOptions" isMulti options={shippingOptionOptions} label="オプションで絞込み" disabled={isSearching} />
            </div>
          </div>
          <div className="card p-2">
            <div className="d-flex align-items-end flex-wrap gap-1">
              <QueryInput paramName="sku" label="SKU検索" disabled={queryParams.keyword} />
              <QueryInput paramName="keyword" label="配送先、宛名、注文番号で検索" disabled={queryParams.sku} />
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-end mb-2 gap-1">
          {!queryParams.sku && <Pagination {...pagination} />}
          <div className="d-flex align-items-center gap-1 text-nowrap">
            <Button onClick={onClickDownloadSampleCsv}>
              <span className="fas fa-download mr-1" />
              サンプルCSVダウンロード
            </Button>
            <ExportButton label="エクスポート" fileName="出荷依頼.csv" rows={rowsForExport} />
            <ImportButton processRows={processRows} />
            <ManualShipmentButtons tenant={tenant} onFinish={updateComponentVersion} />
            <AddButton label="廃棄依頼" color="danger" itemRef={newItemRef} FormModal={ShippingRequestFormModal} initialValues={{ type: 'discard' }} processValues={_ => ({ ..._, status: 'initial', productIds: _.items.map(_ => _.productId), })} formProps={{ tenant, senders, products: chunkProducts, }} />
            <AddButton label="返送依頼" color="warning" itemRef={newItemRef} FormModal={ShippingRequestFormModal} initialValues={{ type: 'return' }} processValues={_ => ({ ..._, status: 'initial', productIds: _.items.map(_ => _.productId), })} formProps={{ tenant, senders, products: chunkProducts, }} />
            <AddButton label="出荷依頼" itemRef={newItemRef} FormModal={ShippingRequestFormModal} initialValues={{}} processValues={processNewValues} formProps={{ tenant, senders, products: chunkProducts, newItems: newItems && newItems.map(_ => ({ ..._, goodQuantity: parseInt(_.goodQuantity, 10) })), }} opensInitially={!isEmpty(newItems)} />
            <AmazonAddButton {...{ tenant, senders, products: chunkProducts, }} />
          </div>
        </div>
        <div className="overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            sortedShippingRequestItems.length > 0 ? (
              <table className="table sticky-table table-sm table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th style={{ minWidth: 120 }}>
                      <QuerySortLabel field="id">
                        出荷ID
                      </QuerySortLabel>
                      <br />
                      出荷依頼日時
                    </th>
                    <th style={{ minWidth: 80 }}>
                      <QuerySortLabel field="status">
                        ステータス
                      </QuerySortLabel>
                      <br />
                      <QuerySortLabel field="completedAt">
                        出荷日
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 120 }}>
                      伝票番号
                      <br />
                      配送方法
                    </th>
                    <th style={{ minWidth: 200 }}>商品</th>
                    <th style={{ minWidth: 50 }}>数量</th>
                    <th style={{ minWidth: 100 }}>
                      <QuerySortLabel field="shippingOptionValues">
                        オプション
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 120 }}>添付ファイル</th>
                    <th style={{ minWidth: 200 }}>配送情報</th>
                    <th style={{ minWidth: 100 }}>配送元</th>
                    <th style={{ minWidth: 180 }}>メモ</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    sortedShippingRequestItems.map((shippingRequestItem) => {
                      const { id, ref, type = 'normal', shippingItems = [], senderId, orderNumber, postalCode, prefecture, city, address1, address2, name, phone, deliveryDate, deliveryTime = 'free', shippingOptionValues = [], codAmount = 0, status, files = [], createdAt, isImmutable, sender, itemsWithProduct, deliveryType, completedAt, plan, isHiddenDeliveryType = false, note, shopifySource, rakutenSource, yahooSource, ttsSource, mercariSource, qoo10Source, isNotMallReady = false, isManuallyShipped = false, } = shippingRequestItem;
                      const rowItems = itemsWithProduct.length > 0 ? itemsWithProduct : [{}];
                      const { color } = statuses[status];
                      const storagePath = `tenants/${tenant.id}/shippingRequests/${id}`;
                      const validateFiles = (files) => {
                        if(Array.from(files).every(_ => _.type === 'application/pdf')) {
                          return true;
                        } else {
                          toast.error('PDFを指定してください');
                          return false;
                        }
                      }
                      const onUploaded = async (uploads) => {
                        await Promise.all(uploads.map(async ({ file, path, downloadUrl, }) => {
                          await ref.set({
                            files: firebase.firestore.FieldValue.arrayUnion({
                              uploadedAt: new Date(),
                              downloadUrl,
                              storagePath: path,
                              ...pick(file, ['name', 'type']),
                            }),
                          }, { merge: true });
                        }));
                      };
                      const processValues = (values) => {
                        return {
                          ...values,
                          productIds: values.items.map(_ => _.productId),
                        };
                      };
                      const onClickDeleteFile = async (file) => {
                        if(!window.confirm('本当に削除しますか？')) return;

                        try {
                          file.storagePath && await storageRef.child(file.storagePath).delete();
                          await ref.set({
                            files: firebase.firestore.FieldValue.arrayRemove({ ...file, }),
                          }, { merge: true });
                          toast.success('削除しました');
                        } catch(e) {
                          console.error(e);
                          toast.error('失敗しました');
                        }
                      };

                      return (
                        <Fragment key={id}>
                          {
                            rowItems.map((item, i) => {
                              const { productId, product, setSource, } = item;

                              return (
                                <tr key={i} className={`table-${color}`}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          <div>{tenant.id}-{id}</div>
                                          <div className="text-muted">{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}</div>
                                          <ShippingRequestTypeBadge type={type} />
                                          {shopifySource != null && <div><span className="badge badge-info">Shopify</span></div>}
                                          {rakutenSource != null && <div><span className="badge badge-info">楽天</span></div>}
                                          {yahooSource != null && <div><span className="badge badge-info">Yahoo</span></div>}
                                          {ttsSource != null && <div><span className="badge badge-info">TikTok Shop</span></div>}
                                          {qoo10Source != null && <div><span className="badge badge-info">Qoo10</span></div>}
                                          {mercariSource != null && <div><span className="badge badge-info">メルカリ</span></div>}
                                          {isNotMallReady && <div><span className="badge badge-warning">モール処理待ち</span></div>}
                                          {isManuallyShipped && <div><span className="badge badge-secondary">手動出荷</span></div>}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div>
                                            {statuses[status].label}
                                            <ShippingRequestStatusComputationDetailButton shippingRequest={shippingRequestItem} />
                                          </div>
                                          <div>
                                            {completedAt && formatDate(completedAt.toDate(), 'yyyy/MM/dd')}
                                          </div>
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            shippingItems.map((shippingItem, i) => {
                                              const { documentNumber, } = shippingItem;
                                              return (
                                                <div key={i}>
                                                  {type === 'amazon' ? computeDocumentNumberForBarcode(shippingRequestItem) : documentNumber}
                                                </div>
                                              );
                                            })
                                          }
                                          <div>{!isHiddenDeliveryType && deliveryTypes[deliveryType].label}</div>
                                          <div>{plan?.ShipmentId}</div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    <ProductTagsDisplay product={product} productTags={[]} />
                                    <div className="text-muted small d-flex gap-1 align-items-center flex-wrap">
                                      <div>{product && product.sku}</div>
                                      {setSource != null && <span className="badge badge-warning">{setSource.sku} セット</span>}
                                    </div>
                                    <div className="d-flex align-items-start gap-1">
                                      {
                                        product?.isAmazonProduct && (
                                          <div>
                                            <img src={smallImageUrl(product?.amazonProduct)} style={{ maxHeight: 40 }} />
                                          </div>
                                        )
                                      }
                                      <div className="flex-grow-1 text-wrap">
                                        <span>{product?.name}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    {numeral(item[types[type].quantityName]).format('0,0')}
                                  </td>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          {fieldDisplayValue(shippingOptionValues, fields({ tenant, type }).shippingOptionValues)}
                                          {
                                            (shippingOptionValues || []).includes('codAll') && (
                                              <div className="mt-1 small text-muted">
                                                代引き金額: {numeral(codAmount).format()}
                                              </div>
                                            )
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            (shippingOptionValues || []).includes('print') && (
                                              <Fragment>
                                                {
                                                  files.map((file) => {
                                                    return (
                                                      <div className="mb-1">
                                                        {file.name}
                                                        <a className="ml-2" href={file.downloadUrl} target="_blank">
                                                          <span className="fas fa-eye" />
                                                        </a>
                                                        <a className="ml-2 text-danger cursor-pointer" onClick={onClickDeleteFile.bind(null, file)}>
                                                          <span className="fas fa-times" />
                                                        </a>
                                                      </div>
                                                    );
                                                  })
                                                }
                                                <FilesUploadButton storagePath={storagePath} onUploaded={onUploaded} accept="application/pdf" validate={validateFiles} />
                                              </Fragment>
                                            )
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div>
                                            {postalCode}
                                            <br />
                                            {prefectures[prefecture]}
                                            {city}
                                            {address1}
                                            {address2}
                                            <br />
                                            {name} {phone}
                                          </div>
                                          {fieldDisplayValue(deliveryDate, fields({ tenant, type }).deliveryDate)}
                                          {fieldDisplayValue(deliveryTime, fields({ tenant, type }).deliveryTime)}
                                          {orderNumber}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.code}
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line', }}>
                                          {note}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div className="d-flex flex-column gap-1 text-nowrap">
                                            {
                                              ['processing', 'shipped'].includes(status) && (
                                                <CancelButton size="sm" item={shippingRequestItem} />
                                              )
                                            }
                                            <EditButton size="sm" itemRef={ref} FormModal={ShippingRequestFormModal} processValues={processValues} formProps={{ tenant, values: shippingRequestItem, senders, products: chunkProducts }} disabled={isImmutable} onFinish={updateComponentVersion} />
                                            <DeleteButton size="sm" item={shippingRequestItem} itemRef={ref} disabled={isImmutable} onFinish={updateComponentVersion} />
                                          </div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                出荷依頼はありません
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});

function CancelButton (props) {
  const { item } = props;
  const [showsModal, toggleModal] = useToggle(false);
  const onClickOwn = async () => {
    if(!window.confirm(dedent`
      ・デフォルトの配送元設定に返送します。
      ・返品/キャンセル手数料300円（税別）が発生します。
      よろしいですか？

      ※ ステータスが【出荷済み】の出荷IDにつきましては、お荷物が既にお届け済み、或いはお届け済み後に弊社にてキャンセル手配になる場合がございますので、お届けした場合は受取人様の元へ引き上げに伺う手配となります。受取人様に事前にご連絡をお願いいたします。
    `)) return;

    await item.ref.update({ status: 'cancelling', returnDestination: 'own', });
    toggleModal(false);
  };
  const onClickLogisp = async () => {
    if(!window.confirm(dedent`
      出荷作業中以降のキャンセルに関しては
      下記手数料が発生致しますがよろしいでしょうか。
      ・着払い代金：実費
      ・着払い建替手数料：300円（税別）
      ・返品／キャンセル手数料：300円（税別）
      上記問題なければ配達停止・返送依頼を致しますが
      よろしいでしょうか。 

      ※ ステータスが【出荷済み】の出荷IDにつきましては、お荷物が既にお届け済み、或いはお届け済み後に弊社にてキャンセル手配になる場合がございますので、お届けした場合は受取人様の元へ引き上げに伺う手配となります。受取人様に事前にご連絡をお願いいたします。
    `)) return;

    await item.ref.update({ status: 'cancelling', returnDestination: 'logisp', });
    toggleModal(false);
  };

  return (
    <Fragment>
      <Button onClick={toggleModal.bind(null, true)}>
        キャンセル申請
      </Button>
      {
        showsModal && (
          <Modal isOpen>
            <ModalHeader>
              キャンセル申請
            </ModalHeader>
            <ModalBody className="text-center">
              <Button onClick={onClickOwn}>
                自社に返送する
              </Button>
              <Button className="ml-2" onClick={onClickLogisp}>
                倉庫に返送する
              </Button>
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" color="secondary" onClick={toggleModal.bind(null, false)}>閉じる</Button>
            </ModalFooter>
          </Modal>
        )
      }
    </Fragment>
  );
}

function AmazonAddButton (props) {
  const { tenant, senders, products, newItems, opensInitially = false, ...extraProps } = props;
  const sendersById = keyBy(senders, 'id');
  const [showsModal, toggleModal] = useToggle(opensInitially);
  const processNewValues = () => {};
  const onSubmit = async (values) => {
    const { items, hasCase, rawItems, plans, extraSellerId, } = values;
    const itemsBySku = keyBy(items, 'sku');
    const sender = omit(sendersById[values.senderId], 'ref');

    try {
      const { data: creationResult } = await createFulfillmentInboundShipmentFromPlan({
        tenantId: tenant.id,
        plans,
        sender,
        extraSellerId,
      });
      await serial(plans, async (plan, i) => {
        console.log(plan);
        const id = await getNextShippingRequestId(tenant.ref, db);
        const planItems = plan.items.map(_ => ({ ...itemsBySku[_.msku], goodQuantity: _.quantity, quantity: _.quantity, ngQuantity: 0, }));
        const ref = tenant.ref.collection('shippingRequests').doc(id)
        await ref.set({
          plan: {
            ...plan,
            ShipmentId: creationResult[i].shipmentConfirmationId,
          },
          ...omit(values, ['plans']),
          postalCode: plan.destination.address.postalCode.replace('-', ''),
          prefecture: prefecturesByName[plan.destination.address.stateOrProvinceCode],
          city: plan.destination.address.city,
          address1: plan.destination.address.addressLine1,
          address2: plan.destination.address.addressLine2 || '',
          name: plan.destination.address.name,
          items: hasCase ? rawItems : planItems,
          status: 'initial',
          productIds: (hasCase ? rawItems : planItems).map(_ => _.productId),
          createdAt: new Date(),
        });
      }, Promise.resolve());
      toast.success('追加しました');
      toggleModal(false);
    } catch(e) {
      console.error(e);
      toast.error(`失敗しました。 (${e.message})`);
      db.collection('debugLogs').add({ location: window.location.href, operationType: 'createAmazonShippingRequest', ...pick(e, ['message', 'stack']), payload: { plans }, createdAt: new Date() });
    }
  };

  return (
    <Fragment>
      <Button color="primary" onClick={toggleModal.bind(null, true)} {...extraProps}>
        <span className="mr-1 fas fa-plus" />
        Amazon納品依頼
      </Button>
      {showsModal && <ShippingRequestFormModal isOpen onSubmit={onSubmit} onClickClose={toggleModal.bind(null, false)} values={{ type: 'amazon' }} type="amazon" {...{ tenant, senders, products, newItems, }} />}
    </Fragment>
  );
}

function ManualShipmentButtons (props) {
  const { tenant, onFinish } = props;
  const [rows, setRows] = useState(null);
  const [isProcessing, toggleProcessing] = useToggle(false);
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const hasErrors = (rows || []).some(_ => !isEmpty(_.errors));
  const hasProcessed = (rows || []).some(_ => _.result != null);
  const onClickDownloadSampleCsv = () => {
    const data = [{
      出荷依頼ID: `${tenant.id}-S1`,
      配送会社: deliveryTypes.yamato.label,
      伝票番号: '123456789012',
      出荷日: formatDate(new Date(), 'yyyy/MM/dd'),
    }];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, '出荷済み一括登録サンプル.csv');
  };
  const validate = async (row) => {
    const { 出荷依頼ID: _id = '', 配送会社: deliveryTypeLabel = '', 伝票番号: _documentNumbers = '', 出荷日: _shippedOn = '', } = row;
    const id = _id.trim().replace(`${tenant.id}-`, '');
    const shippingRequest = isEmpty(id) ? null : await getDocumentData(shippingRequestsRef.doc(id), true);
    const directedDeliveryType = manualShipmentDeliveryTypesByLabel[deliveryTypeLabel.trim()]?.key;
    const documentNumbers = _documentNumbers.split(',').map(_ => _.trim()).filter(_ => _);
    const shippedOn = isEmpty(_shippedOn.trim()) ? new Date() : parseDate(_shippedOn.trim(), 'yyyy/MM/dd', new Date());
    const errors = [
      !shippingRequest && '出荷依頼IDが正しくありません',
      shippingRequest && (shippingRequest.type ?? 'normal') !== 'normal' && '出荷依頼以外は登録できません',
      shippingRequest && shippingRequest.status !== 'waitingToReceive' && '入荷待ちの出荷依頼のみ登録できます',
      !directedDeliveryType && '配送会社が正しくありません',
      isEmpty(documentNumbers) && '伝票番号は必須です',
      !isValidDate(shippedOn) && '出荷日が正しくありません',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      shippingRequest,
      directedDeliveryType,
      documentNumbers,
      shippedOn,
    };
  };
  const processRows = async (rows) => {
    const validatedRows = await Promise.all(rows.map(validate));
    const rowsGroupedById = groupBy(validatedRows.filter(_ => _.shippingRequest), _ => _.shippingRequest.id);
    setRows(validatedRows.map(_ => ({
      ..._,
      errors: [..._.errors, rowsGroupedById[_.shippingRequest?.id]?.length > 1 && '出荷依頼IDが重複しています'].filter(_ => _),
    })));
  };
  const ship = async (row) => {
    const { shippingRequest: { ref }, directedDeliveryType, documentNumbers, shippedOn, } = row;
    const latest = await getDocumentData(ref, true);
    if(latest?.status !== 'waitingToReceive') return { result: 'failed', message: 'ステータスが入荷待ちではなくなりました' };

    try {
      await ref.update({
        directedDeliveryType,
        shippingItems: documentNumbers.map(documentNumber => ({ documentNumber, shippedOn })),
        status: 'shipped',
        completedAt: shippedOn,
        isManuallyShipped: true,
        manuallyShippedAt: new Date(),
        allocatedInventoryIds: firebase.firestore.FieldValue.delete(),
      });
      return { result: 'succeeded' };
    } catch(e) {
      console.error(e);
      return { result: 'failed', message: e.message };
    }
  };
  const onClickShip = async () => {
    if(!window.confirm(`${rows.length}件を出荷済みにします。よろしいですか？`)) return;

    toggleProcessing(true);
    const results = await serial(rows, async (row, i) => {
      setRows(rows => rows.map((_, j) => j === i ? { ..._, result: 'processing' } : _));
      const result = await ship(row);
      setRows(rows => rows.map((_, j) => j === i ? { ..._, ...result } : _));
      return result;
    });
    toggleProcessing(false);
    const failedCount = results.filter(_ => _.result === 'failed').length;
    failedCount > 0 ? toast.error(`${failedCount}件失敗しました`) : toast.success('出荷済みにしました');
    onFinish();
  };

  return (
    <Fragment>
      <Button onClick={onClickDownloadSampleCsv}>
        <span className="fas fa-download mr-1" />
        出荷済み一括登録サンプル
      </Button>
      <ImportButton label="出荷済み一括登録" processRows={processRows} successMessage="読み込みました" />
      {
        rows != null && (
          <Modal isOpen size="xl">
            <ModalHeader>
              出荷済み一括登録
            </ModalHeader>
            <ModalBody>
              <div className="small text-muted mb-2">
                LOGISP倉庫以外から出荷した入荷待ちの出荷依頼を、手動で出荷済みにします。モールへの発送通知は行いませんので、モール側で発送処理をしてください。
              </div>
              <div className="overflow-auto">
                <table className="table table-sm table-bordered">
                  <thead className="thead-light text-center text-nowrap">
                    <tr>
                      <th>出荷依頼ID</th>
                      <th>注文番号</th>
                      <th>宛名</th>
                      <th>配送会社</th>
                      <th>伝票番号</th>
                      <th>出荷日</th>
                      <th>結果</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      rows.map((row, i) => {
                        const { source, errors, shippingRequest, directedDeliveryType, documentNumbers, shippedOn, result, message, } = row;
                        return (
                          <tr key={i} className={classnames({ 'table-danger': !isEmpty(errors) || result === 'failed', 'table-success': result === 'succeeded', })}>
                            <td className="text-nowrap">{source['出荷依頼ID']}</td>
                            <td>{shippingRequest?.orderNumber}</td>
                            <td>{shippingRequest?.name}</td>
                            <td className="text-nowrap">{deliveryTypes[directedDeliveryType]?.label ?? source['配送会社']}</td>
                            <td>{documentNumbers.join(', ')}</td>
                            <td className="text-nowrap">{isValidDate(shippedOn) ? formatDate(shippedOn, 'yyyy/MM/dd') : source['出荷日']}</td>
                            <td>
                              {errors.map((_, i) => <div key={i} className="text-danger small">{_}</div>)}
                              {result === 'processing' && <span className="fas fa-spin fa-spinner" />}
                              {result === 'succeeded' && '出荷済み'}
                              {result === 'failed' && <div className="text-danger small">失敗: {message}</div>}
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" color="secondary" onClick={setRows.bind(null, null)} disabled={isProcessing}>閉じる</Button>
              <Button color="primary" onClick={onClickShip} disabled={hasErrors || hasProcessed || isProcessing}>
                {isProcessing && <span className="fas fa-spin fa-spinner mr-1" />}
                出荷済みにする
              </Button>
            </ModalFooter>
          </Modal>
        )
      }
    </Fragment>
  );
}
